"use client";

import { useMemo, useState } from "react";

import type { AccountReading } from "./account-api";
import { formatAccountDate } from "./account-format";

type ReadingFilter = "all" | "career" | "relationships" | "other";

const FILTER_LABELS: Record<ReadingFilter, string> = {
  all: "All",
  career: "Career",
  relationships: "Relationships",
  other: "Other",
};

function readingFilter(reading: AccountReading): Exclude<ReadingFilter, "all"> {
  const subject = `${reading.active_concern ?? ""} ${reading.title}`.toLowerCase();
  if (/career|job|work|business/.test(subject)) return "career";
  if (/marriage|relationship|love|partner/.test(subject)) return "relationships";
  return "other";
}

function ReadingRow({ reading }: { reading: AccountReading }) {
  const lastAt = reading.last_message_at || reading.created_at;
  return (
    <article className="relative border-l border-[var(--color-accent-border)] pb-5 pl-5 last:pb-0">
      <span
        aria-hidden
        className="absolute -left-1.5 top-1 h-3 w-3 rounded-full border-2 border-[var(--color-surface-raised)] bg-[var(--color-accent)]"
      />
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-4 shadow-[var(--shadow-elevation-low)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-[var(--color-accent)]">
              {FILTER_LABELS[readingFilter(reading)]}
              {lastAt ? ` · ${formatAccountDate(lastAt)}` : ""}
            </p>
            <h3 className="mt-1 font-serif text-lg text-[var(--color-text-main)]">
              {reading.title}
            </h3>
            <p className="mt-1 text-xs text-[var(--color-text-sub)]">
              {reading.assigned_jyotishi || "AI Jyotishi"}
              {reading.conversation_turns ? ` · ${reading.conversation_turns} questions` : ""}
            </p>
            {reading.last_message ? (
              <p className="mt-2 line-clamp-2 text-sm leading-6 text-[var(--color-text-sub)]">
                {reading.last_message}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 gap-2 sm:flex-col sm:items-stretch">
            <a
              className="inline-flex min-h-11 items-center justify-center rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-white"
              href={reading.chat_url}
            >
              Continue
            </a>
            {!reading.report_type ? (
              <a
                className="inline-flex min-h-11 items-center justify-center rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-semibold text-[var(--color-primary)]"
                href={`/kundali/${encodeURIComponent(reading.reading_id)}/${
                  reading.language === "hi" ? "?lang=hi" : ""
                }`}
              >
                View kundali
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

export function AccountReadingLibrary({ readings }: { readings: AccountReading[] }) {
  const [filter, setFilter] = useState<ReadingFilter>("all");
  const availableFilters = useMemo(
    () =>
      (["all", "career", "relationships", "other"] as const).filter(
        (candidate) =>
          candidate === "all" || readings.some((item) => readingFilter(item) === candidate)
      ),
    [readings]
  );
  const visible =
    filter === "all" ? readings : readings.filter((item) => readingFilter(item) === filter);

  if (!readings.length) return null;
  return (
    <section className="mb-8" aria-labelledby="reading-library-heading">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-[var(--color-accent)]">
            Saved readings
          </p>
          <h2 id="reading-library-heading" className="mt-1 font-serif text-2xl">
            Your consultation timeline
          </h2>
        </div>
        {availableFilters.length > 2 ? (
          <div className="flex flex-wrap gap-2" aria-label="Filter saved readings">
            {availableFilters.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={filter === option}
                className={`min-h-11 rounded-full border px-4 py-2 text-xs font-semibold ${
                  filter === option
                    ? "border-[var(--color-accent)] bg-[var(--color-accent-light)] text-[var(--color-accent)]"
                    : "border-[var(--color-border)] text-[var(--color-text-sub)]"
                }`}
                onClick={() => setFilter(option)}
              >
                {FILTER_LABELS[option]}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div>
        {visible.map((reading) => (
          <ReadingRow key={reading.reading_id} reading={reading} />
        ))}
      </div>
    </section>
  );
}
